import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { safeJSONParse } from "./helper";

// Helper function to decode HTML entities
const decodeHTMLEntities = (text: string): string => {
  const entities: { [key: string]: string } = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&apos;": "'",
    "&#x27;": "'",
    "&#x2F;": "/",
    "&nbsp;": " ",
  };

  return text.replace(/&[#\w]+;/g, (entity) => entities[entity] || entity);
};

// Helper function to clean text for PDF (remove JSON artifacts and formatting)
export const cleanTextForPDF = (text: string): string => {
  if (!text) return "";

  let cleaned = String(text)
    .replace(/\\n/g, "\n") // Convert escaped newlines
    .replace(/\\"/g, '"') // Convert escaped quotes
    .replace(/\\t/g, " ") // Convert tabs to spaces
    .replace(/\\/g, "") // Remove remaining backslashes
    .trim();

  // Decode HTML entities
  cleaned = decodeHTMLEntities(cleaned);

  // Remove excessive whitespace but preserve single line breaks
  cleaned = cleaned.replace(/[ \t]+/g, " "); // Multiple spaces/tabs to single space
  cleaned = cleaned.replace(/\n\s*\n\s*\n/g, "\n\n"); // Max 2 consecutive newlines

  return cleaned.trim();
};

// Helper function to add PDF header
export const addPDFHeader = (
  pdf: jsPDF,
  agentName: string,
  margin: number,
  pageWidth: number
) => {
  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  pdf.text(`${agentName}`, margin, margin);

  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");
  const dateOnly = new Date().toLocaleDateString();
  pdf.text(`Generated: ${dateOnly}`, margin, margin + 10);

  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, margin + 15, pageWidth - margin, margin + 15);
};

// Helper function to normalize text for PDF rendering
const normalizeTextForPDF = (text: string): string => {
  // Replace problematic Unicode characters with ASCII equivalents
  return text
    .replace(/[\u2018\u2019]/g, "'")  // Smart single quotes
    .replace(/[\u201C\u201D]/g, '"')  // Smart double quotes
    .replace(/[\u2013\u2014]/g, "-")  // En/Em dashes
    .replace(/\u2026/g, "...")        // Ellipsis
    .replace(/[\u2022\u25E6\u25AA\u25AB]/g, "-")  // Bullets
    .replace(/\u00A0/g, " ")          // Non-breaking space
    .replace(/[\u200B-\u200D\uFEFF]/g, "")  // Zero-width characters
    .replace(/[^\x20-\x7E\n\r\t]/g, (char) => {
      // For any remaining non-ASCII characters, try to keep printable ones
      const code = char.charCodeAt(0);
      // Keep common Latin extended characters
      if (code >= 0x00A1 && code <= 0x00FF) return char;
      return "";  // Remove other problematic characters
    });
};

// Helper function to generate text-based PDF
export const generateTextPDF = (
  pdf: jsPDF,
  formattedText: string,
  margin: number,
  maxWidth: number,
  pageHeight: number,
  lineHeight: number,
  agentId?: string
) => {
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");

  // Normalize text to handle special characters
  const normalizedText = normalizeTextForPDF(formattedText);

  const lines = pdf.splitTextToSize(normalizedText, maxWidth);
  // Start at top for redrafter_orchestrator (no header), otherwise below header
  let y = agentId === "redrafter_orchestrator" ? margin : margin + 25;
  const bottomMargin = margin + 10;

  lines.forEach((line: string) => {
    if (y + lineHeight > pageHeight - bottomMargin) {
      pdf.addPage();
      y = margin + 10;
    }
    pdf.text(line, margin, y);
    y += lineHeight;
  });
};

// Helper function to check if text contains Arabic characters
export const hasArabicCharacters = (text: string): boolean => {
  return /[\u0600-\u06FF]/.test(text);
};

// Helper function to generate mixed content PDF using html2canvas (for Arabic + English)
export const generateMixedContentPDF = async (
  pdf: jsPDF,
  formattedText: string,
  margin: number,
  pageWidth: number,
  pageHeight: number,
  agentId: string
): Promise<void> => {
  const paragraphs = formattedText.split("\n").filter((line) => line.trim());

  const bottomPadding = 20;
  const topPadding = 25;
  const firstPageAvailable = pageHeight - topPadding - margin - bottomPadding;
  const otherPageAvailable = pageHeight - margin - bottomPadding - 10;

  let currentY = margin + topPadding;
  let isFirstPage = true;
  let currentBatch: string[] = [];

  const renderBatch = async (
    batch: string[],
    yPosition: number
  ): Promise<number> => {
    return new Promise((resolve, reject) => {
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      tempDiv.style.fontFamily = "Arial, Tahoma, sans-serif";
      tempDiv.style.fontSize = "14px";
      tempDiv.style.lineHeight = "1.6";
      tempDiv.style.width = "600px";
      tempDiv.style.padding = "20px";
      tempDiv.style.background = "#ffffff";
      tempDiv.style.color = "#000000";

      tempDiv.innerHTML = batch
        .map((p) => {
          const isArabic = hasArabicCharacters(p);
          const direction = isArabic ? "rtl" : "ltr";
          const textAlign = isArabic ? "right" : "left";
          return `<p style="margin: 0 0 15px 0; direction: ${direction}; text-align: ${textAlign};">${p}</p>`;
        })
        .join("");

      document.body.appendChild(tempDiv);

      html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      })
        .then((canvas) => {
          const imgWidth = pageWidth - 2 * margin;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          const imgData = canvas.toDataURL("image/png");

          pdf.addImage(imgData, "PNG", margin, yPosition, imgWidth, imgHeight);
          document.body.removeChild(tempDiv);
          resolve(imgHeight);
        })
        .catch((error) => {
          document.body.removeChild(tempDiv);
          reject(error);
        });
    });
  };

  for (let i = 0; i < paragraphs.length; i++) {
    const testBatch = [...currentBatch, paragraphs[i]];

    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    tempDiv.style.fontFamily = "Arial, Tahoma, sans-serif";
    tempDiv.style.fontSize = "14px";
    tempDiv.style.lineHeight = "1.6";
    tempDiv.style.width = "600px";
    tempDiv.style.padding = "20px";
    tempDiv.style.background = "#ffffff";
    tempDiv.style.color = "#000000";

    tempDiv.innerHTML = testBatch
      .map((p) => {
        const isArabic = hasArabicCharacters(p);
        const direction = isArabic ? "rtl" : "ltr";
        const textAlign = isArabic ? "right" : "left";
        return `<p style="margin: 0 0 15px 0; direction: ${direction}; text-align: ${textAlign};">${p}</p>`;
      })
      .join("");

    document.body.appendChild(tempDiv);
    const testHeight = tempDiv.offsetHeight;
    document.body.removeChild(tempDiv);

    const imgWidth = pageWidth - 2 * margin;
    const estimatedPdfHeight = (testHeight * imgWidth) / 600;

    const availableHeight = isFirstPage
      ? firstPageAvailable
      : otherPageAvailable;

    if (estimatedPdfHeight > availableHeight && currentBatch.length > 0) {
      try {
        await renderBatch(currentBatch, currentY);
      } catch (error) {
        return Promise.reject(error);
      }

      pdf.addPage();
      currentY = margin + 10;
      isFirstPage = false;
      currentBatch = [paragraphs[i]];
    } else {
      currentBatch = testBatch;
    }
  }

  if (currentBatch.length > 0) {
    try {
      await renderBatch(currentBatch, currentY);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  return Promise.resolve();
};

// Helper function to format content for PDF based on agent type
export const formatContentForPDF = (
  content: string,
  agentId?: string
): string => {
  try {
    const parsedContent = safeJSONParse(content);
    const finalAnswer = safeJSONParse(parsedContent?.finalAnswer);
    const question = parsedContent?.question;

    if (agentId === "utility_agent") {
      return parsedContent?.finalAnswer?.toString() || content;
    }

    if (agentId === "press_release_agent") {
      let formatted = "";

      // Headline
      if (finalAnswer?.payload?.headline) {
        formatted += `${cleanTextForPDF(finalAnswer.payload.headline)}\n\n`;
      }

      // Subheadline
      if (finalAnswer?.payload?.subheadline) {
        formatted += `${cleanTextForPDF(finalAnswer.payload.subheadline)}\n\n`;
      }

      // Date
      if (finalAnswer?.payload?.date) {
        formatted += `${cleanTextForPDF(finalAnswer.payload.date)}\n\n`;
      }

      // Body paragraphs
      if (Array.isArray(finalAnswer?.payload?.body)) {
        finalAnswer.payload.body.forEach((body: string) => {
          if (body && body.trim()) {
            formatted += `${cleanTextForPDF(body)}\n\n`;
          }
        });
      }

      // Boilerplate
      if (finalAnswer?.payload?.boiler_plate) {
        if (finalAnswer.payload.boiler_plate.title) {
          formatted += `${cleanTextForPDF(finalAnswer.payload.boiler_plate.title)}\n\n`;
        }
        if (finalAnswer.payload.boiler_plate.body) {
          formatted += `${cleanTextForPDF(finalAnswer.payload.boiler_plate.body)}\n\n`;
        }
      }

      // Media contact
      if (finalAnswer?.payload?.media_contact) {
        formatted += `${cleanTextForPDF(finalAnswer.payload.media_contact)}`;
      }

      return formatted.trim();
    }

    if (agentId === "LocalizerAgent" || agentId === "localizer_agent") {
      let formatted = "";

      // Arabic headline
      if (finalAnswer?.payload?.headline_ar) {
        formatted += `${finalAnswer.payload.headline_ar}\n\n`;
      }

      // Arabic subheadline
      if (finalAnswer?.payload?.subheadline_ar) {
        formatted += `${finalAnswer.payload.subheadline_ar}\n\n`;
      }

      // Date
      if (finalAnswer?.payload?.date) {
        formatted += `${finalAnswer.payload.date}\n\n`;
      }

      // Arabic body paragraphs
      if (Array.isArray(finalAnswer?.payload?.body_ar)) {
        finalAnswer.payload.body_ar.forEach((body: string) => {
          if (body && body.trim()) {
            formatted += `${body}\n\n`;
          }
        });
      }

      // Media contact
      if (finalAnswer?.payload?.media_contact) {
        formatted += `${finalAnswer.payload.media_contact}`;
      }

      return formatted.trim();
    }

    if (agentId === "media_brief_agent") {
      const mediaBreiefPayload = finalAnswer?.payload;
      let formatted = "";

      formatted += "MEDIA BRIEFING: PRESS RELEASE Q&A\n\n";

      // Interviewee Profile
      if (mediaBreiefPayload?.interviewee_profile) {
        formatted += "Interviewee Profile:\n";
        formatted += `${cleanTextForPDF(mediaBreiefPayload.interviewee_profile)}\n\n`;
      }

      // Strategic Narrative
      if (mediaBreiefPayload?.strategic_narrative) {
        formatted += "Strategic Narrative (Top-Level Messaging):\n";
        formatted += `${cleanTextForPDF(mediaBreiefPayload.strategic_narrative)}\n\n`;
      }

      // Key Strategic Points
      if (Array.isArray(mediaBreiefPayload?.key_strategic_points) && mediaBreiefPayload.key_strategic_points.length > 0) {
        formatted += "Key Strategic Points:\n";
        mediaBreiefPayload.key_strategic_points.forEach((point: string) => {
          if (point && point.trim()) {
            formatted += `• ${cleanTextForPDF(point)}\n`;
          }
        });
        formatted += "\n";
      }

      // Q&A Section
      if (Array.isArray(mediaBreiefPayload?.qa_set) && mediaBreiefPayload.qa_set.length > 0) {
        formatted += "QUESTIONS & ANSWERS\n\n";
        mediaBreiefPayload.qa_set.forEach((qa: any, index: number) => {
          const questionNumber = qa.question_number || index + 1;

          if (qa.question) {
            formatted += `Q${questionNumber}: ${cleanTextForPDF(qa.question)}\n`;
          }

          if (qa.answer) {
            const riskLevel = qa.risk_level || 'LOW';

            // Handle answer based on risk level
            if (riskLevel === 'MEDIUM' || riskLevel === 'HIGH') {
              // For MEDIUM or HIGH risk
              if (Array.isArray(qa.answer)) {
                // Answer is an array - map through each option
                qa.answer.forEach((ans: any, ansIdx: number) => {
                  const answerText = ans.text || ans;
                  if (qa.answer.length > 1) {
                    // Multiple options: show with strategy label
                    const strategyUsed = ans.strategy_used || 'N/A';
                    formatted += `A${questionNumber} (Option ${ansIdx + 1} - ${strategyUsed}): ${cleanTextForPDF(answerText)}\n`;
                  } else {
                    // Single option: no label needed
                    formatted += `A${questionNumber}: ${cleanTextForPDF(answerText)}\n`;
                  }
                });
              } else {
                // Not an array: just show as is
                formatted += `A${questionNumber}: ${cleanTextForPDF(qa.answer)}\n`;
              }
            } else {
              // For LOW risk: just show first option's text
              if (Array.isArray(qa.answer)) {
                const answerText = qa.answer[0]?.text || qa.answer[0] || qa.answer;
                formatted += `A${questionNumber}: ${cleanTextForPDF(answerText)}\n`;
              } else {
                formatted += `A${questionNumber}: ${cleanTextForPDF(qa.answer)}\n`;
              }
            }
          }

          if (qa.risk_level) {
            if (qa.risk_level === 'MEDIUM' || qa.risk_level === 'HIGH') {
              formatted += `High Risk: Potentially sensitive\n`;
            } else if (qa.risk_level === 'LOW') {
              formatted += `Low Risk\n`;
            }
          }
          formatted += `\n`;
        });
      }

      return formatted.trim();
    }

    if (agentId === "social_media_insights_agent") {
      const twitterAnswer = safeJSONParse(parsedContent?.twitterAnswer);
      const linkedinAnswer = safeJSONParse(parsedContent?.linkedinAnswer);
      const instagramAnswer = safeJSONParse(parsedContent?.instagramAnswer);

      const socialMediaLocalizer = safeJSONParse(
        parsedContent?.socialMediaLocalizer
      );
      const twitterLocalizer = safeJSONParse(
        socialMediaLocalizer?.rawTwitterLoc
      );
      const linkedinLocalizer = safeJSONParse(
        socialMediaLocalizer?.rawLinkedinLoc
      );
      const instagramLocalizer = safeJSONParse(
        socialMediaLocalizer?.rawInstagramLoc
      );

      let formatted = "SOCIAL MEDIA POSTS\n\n";
      let hasPlatformContent = false;

      // X (Twitter) Posts - only add header if posts exist
      if (Array.isArray(twitterAnswer?.payload?.posts) && twitterAnswer.payload.posts.length > 0) {
        hasPlatformContent = true;
        formatted += "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n";
        formatted += "X (TWITTER)\n";
        formatted += "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n";

        twitterAnswer.payload.posts.forEach((post: any, idx: number) => {
          if (post.option_number || idx >= 0) {
            formatted += `Post ${post.option_number || idx + 1}`;
            if (post.focus_area) {
              formatted += `: ${cleanTextForPDF(post.focus_area)}`;
            }
            formatted += `\n`;
          }
          if (post.text) {
            formatted += `${cleanTextForPDF(post.text)}\n`;
          }
          if (post.read_more) {
            formatted += `${cleanTextForPDF(post.read_more)}\n`;
          }
          if (Array.isArray(post.hashtags) && post.hashtags.length > 0) {
            formatted += `${post.hashtags.join(" ")}\n`;
          }

          if (twitterLocalizer?.payload?.platforms?.[0]?.posts?.[idx]?.text) {
            formatted += `\nArabic Version:\n${twitterLocalizer.payload.platforms[0].posts[idx].text}\n`;
          }
          formatted += `\n`;
        });
      }

      // LinkedIn Posts - only add header if posts exist
      if (Array.isArray(linkedinAnswer?.payload?.posts) && linkedinAnswer.payload.posts.length > 0) {
        hasPlatformContent = true;
        formatted += "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n";
        formatted += "LINKEDIN\n";
        formatted += "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n";

        linkedinAnswer.payload.posts.forEach((post: any, idx: number) => {
          if (post.option_number || idx >= 0) {
            formatted += `Post ${post.option_number || idx + 1}`;
            if (post.focus_area) {
              formatted += `: ${cleanTextForPDF(post.focus_area)}`;
            }
            formatted += `\n`;
          }
          if (post.text) {
            formatted += `${cleanTextForPDF(post.text)}\n`;
          }
          if (post.read_more) {
            formatted += `${cleanTextForPDF(post.read_more)}\n`;
          }
          if (Array.isArray(post.hashtags) && post.hashtags.length > 0) {
            formatted += `${post.hashtags.join(" ")}\n`;
          }

          if (linkedinLocalizer?.payload?.platforms?.[0]?.posts?.[idx]?.text) {
            formatted += `\nArabic Version:\n${linkedinLocalizer.payload.platforms[0].posts[idx].text}\n`;
          }
          formatted += `\n`;
        });
      }

      // Instagram Posts - only add header if posts exist
      if (Array.isArray(instagramAnswer?.payload?.posts) && instagramAnswer.payload.posts.length > 0) {
        hasPlatformContent = true;
        formatted += "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n";
        formatted += "INSTAGRAM\n";
        formatted += "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n";

        instagramAnswer.payload.posts.forEach((post: any, idx: number) => {
          if (post.option_number || idx >= 0) {
            formatted += `Post ${post.option_number || idx + 1}`;
            if (post.focus_area) {
              formatted += `: ${cleanTextForPDF(post.focus_area)}`;
            }
            formatted += `\n`;
          }
          if (post.text) {
            formatted += `${cleanTextForPDF(post.text)}\n`;
          }
          if (post.read_more) {
            formatted += `${cleanTextForPDF(post.read_more)}\n`;
          }
          if (Array.isArray(post.hashtags) && post.hashtags.length > 0) {
            formatted += `${post.hashtags.join(" ")}\n`;
          }

          if (instagramLocalizer?.payload?.platforms?.[0]?.posts?.[idx]?.text) {
            formatted += `\nArabic Version:\n${instagramLocalizer.payload.platforms[0].posts[idx].text}\n`;
          }
          formatted += `\n`;
        });
      }

      // If no platform has content, return a message
      if (!hasPlatformContent) {
        formatted += "No social media posts available.\n";
      }

      return formatted.trim();
    }

    if (agentId === "internal_communication_agent") {
      let formatted = "";

      // Subject line
      if (finalAnswer?.payload?.email?.subject) {
        formatted += `Subject: ${cleanTextForPDF(finalAnswer.payload.email.subject)}\n\n`;
      }

      // Greeting
      if (finalAnswer?.payload?.email?.greeting) {
        formatted += `${cleanTextForPDF(finalAnswer.payload.email.greeting)}\n\n`;
      }

      // Body paragraphs
      if (Array.isArray(finalAnswer?.payload?.email?.body_paragraphs)) {
        finalAnswer.payload.email.body_paragraphs.forEach((body: string) => {
          if (body && body.trim()) {
            formatted += `${cleanTextForPDF(body)}\n\n`;
          }
        });
      }

      // Closing
      if (finalAnswer?.payload?.email?.closing) {
        formatted += `${cleanTextForPDF(finalAnswer.payload.email.closing)}\n\n`;
      }

      // Signature
      if (finalAnswer?.payload?.email?.signature_block) {
        formatted += `${cleanTextForPDF(finalAnswer.payload.email.signature_block)}`;
      }

      return formatted.trim();
    }

    if (agentId === "synthetic_resume_agent" || agentId === "skill_inference_agent" || agentId === "jd_composer_agent" || agentId === "modify_requirements" || agentId === "jd_details_agent") {
      // Parse the answer field first
      const answer = safeJSONParse(parsedContent?.answer);

      // Check if data is inside an 'output' field (HR workflow format)
      let outputData = answer;
      if (typeof parsedContent?.output === "string") {
        outputData = safeJSONParse(parsedContent.output);
      } else if (parsedContent?.output && typeof parsedContent.output === "object") {
        outputData = parsedContent.output;
      }

      // Parse skill_inference_agent or jd_composer_agent data from answer or output
      let skillInferenceData = {};
      let isSkillInferenceWorkflow = false; // Detect if this is from Skill Inference Agent workflow

      if (typeof answer?.skill_inference_agent === "string") {
        skillInferenceData = safeJSONParse(answer.skill_inference_agent);
        isSkillInferenceWorkflow = true;
      } else if (typeof outputData?.skill_inference_agent === "string") {
        skillInferenceData = safeJSONParse(outputData.skill_inference_agent);
        isSkillInferenceWorkflow = true;
      } else if (answer?.skill_inference_agent) {
        skillInferenceData = answer.skill_inference_agent;
        isSkillInferenceWorkflow = true;
      } else if (outputData?.skill_inference_agent) {
        skillInferenceData = outputData.skill_inference_agent;
        isSkillInferenceWorkflow = true;
      } else if (typeof answer?.jd_composer_agent === "string") {
        skillInferenceData = safeJSONParse(answer.jd_composer_agent);
      } else if (typeof outputData?.jd_composer_agent === "string") {
        skillInferenceData = safeJSONParse(outputData.jd_composer_agent);
      } else if (answer?.jd_composer_agent) {
        skillInferenceData = answer.jd_composer_agent;
      } else if (outputData?.jd_composer_agent) {
        skillInferenceData = outputData.jd_composer_agent;
      }

      // Parse jd_details_agent data (contains overview) from answer or output
      let jdDetailsData = {};
      if (typeof answer?.jd_details_agent === "string") {
        jdDetailsData = safeJSONParse(answer.jd_details_agent);
      } else if (typeof outputData?.jd_details_agent === "string") {
        jdDetailsData = safeJSONParse(outputData.jd_details_agent);
      } else if (answer?.jd_details_agent) {
        jdDetailsData = answer.jd_details_agent;
      } else if (outputData?.jd_details_agent) {
        jdDetailsData = outputData.jd_details_agent;
      }

      // Get all fields from jd_details_agent
      const overview = jdDetailsData?.overview || answer?.overview;
      const responsibilities = jdDetailsData?.responsibilities || answer?.responsibilities;
      const job_title = jdDetailsData?.job_title || answer?.job_title;
      const location = jdDetailsData?.location || answer?.location;
      const about_organization = jdDetailsData?.about_organization || answer?.about_organization;
      const qualifications = jdDetailsData?.qualifications || answer?.qualifications;
      const what_we_look_for = jdDetailsData?.what_we_look_for || answer?.what_we_look_for;
      const why_g42 = jdDetailsData?.why_g42 || answer?.why_g42;
      const application = jdDetailsData?.application || answer?.application;

      // Merge skills data - use skillInferenceData if available, otherwise fallback to answer
      const mergedData = {
        ...(Object.keys(skillInferenceData).length > 0 ? skillInferenceData : answer),
        overview: overview,
        responsibilities: responsibilities,
        job_title: job_title,
        location: location,
        about_organization: about_organization,
        qualifications: qualifications,
        what_we_look_for: what_we_look_for,
        why_g42: why_g42,
        application: application,
      };

      let formatted = "";

      // Note: Header is now added separately via addPDFHeader, so we don't include it here

      // Job Title and Location
      if (mergedData?.job_title) {
        formatted += `Job Title: ${cleanTextForPDF(mergedData.job_title)}\n`;
      }
      if (mergedData?.location) {
        formatted += `Location: ${cleanTextForPDF(mergedData.location)}\n`;
        formatted += "\n";
      }

      // About the Organization
      if (mergedData?.about_organization) {
        formatted += "About the Organization:\n";
        formatted += cleanTextForPDF(mergedData.about_organization) + "\n\n";
      }

      // Overview - shown after user confirms synthetic profile
      if (mergedData?.overview) {
        formatted += "Overview:\n";
        if (Array.isArray(mergedData.overview)) {
          mergedData.overview.forEach((item: string) => {
            formatted += cleanTextForPDF(item) + "\n";
          });
        } else {
          formatted += cleanTextForPDF(mergedData.overview) + "\n";
        }
        formatted += "\n";
      }

      // Responsibilities
      if (Array.isArray(mergedData?.responsibilities) && mergedData.responsibilities.length > 0) {
        formatted += "Responsibilities:\n";
        mergedData.responsibilities.forEach((responsibility: string) => {
          formatted += `  • ${cleanTextForPDF(responsibility)}\n`;
        });
        formatted += "\n";
      }

      // Requirements (from qualifications field)
      if (Array.isArray(mergedData?.qualifications) && mergedData.qualifications.length > 0) {
        formatted += "Requirements:\n";
        mergedData.qualifications.forEach((qualification: string) => {
          formatted += `  • ${cleanTextForPDF(qualification)}\n`;
        });
        formatted += "\n";
      }

      // Functional/Technical Skills
      if (
        Array.isArray(mergedData?.functional_skills) &&
        mergedData.functional_skills.length > 0
      ) {
        formatted += "FUNCTIONAL / TECHNICAL SKILLS\n";
        formatted += "-".repeat(60) + "\n";
        formatted += "Core Technical Skills:\n";
        mergedData.functional_skills.forEach((skill: string) => {
          formatted += `  • ${cleanTextForPDF(skill)}\n`;
        });
        formatted += "\n";
      }

      // Future Skills
      if (
        Array.isArray(mergedData?.future_skills) &&
        mergedData.future_skills.length > 0
      ) {
        formatted += "Future Ready Skills:\n";
        mergedData.future_skills.forEach((skill: string) => {
          formatted += `  • ${cleanTextForPDF(skill)}\n`;
        });
        formatted += "\n";
      }

      // Required Qualifications - Hidden only for Skill Inference Agent
      if (!isSkillInferenceWorkflow && agentId !== "skill_inference_agent" && mergedData?.required_qualifications) {
        formatted += "REQUIRED QUALIFICATIONS\n";
        formatted += "-".repeat(60) + "\n";
        if (mergedData.required_qualifications.education) {
          formatted += `  Education: ${cleanTextForPDF(mergedData.required_qualifications.education)}\n`;
        }
        if (mergedData.required_qualifications.experience !== undefined) {
          formatted += `  Experience: ${cleanTextForPDF(mergedData.required_qualifications.experience)}\n`;
        }
        if (mergedData.required_qualifications.location) {
          formatted += `  Location: ${cleanTextForPDF(mergedData.required_qualifications.location)}\n`;
        }
        if (mergedData.required_qualifications.previous_company) {
          formatted += `  Previous Company: ${cleanTextForPDF(mergedData.required_qualifications.previous_company)}\n`;
        }
        formatted += "\n";
      }

      // Preferred Qualifications - Array format - Hidden only for Skill Inference Agent
      if (
        !isSkillInferenceWorkflow &&
        agentId !== "skill_inference_agent" &&
        Array.isArray(mergedData?.preferred_qualifications) &&
        mergedData.preferred_qualifications.length > 0
      ) {
        formatted += "PREFERRED QUALIFICATIONS\n";
        formatted += "-".repeat(60) + "\n";
        mergedData.preferred_qualifications.forEach((item: any) => {
          formatted += `  • ${cleanTextForPDF(item.entity_type)}: ${cleanTextForPDF(item.entity_value)}\n`;
        });
        formatted += "\n";
      }

      // Screening Questions - REMOVED (Not needed for skill inference agent)
      // Scoring Questions are excluded from PDF export

      // Scoring Weights (if available)
      if (
        mergedData?.scoring_weights &&
        Object.keys(mergedData.scoring_weights).length > 0
      ) {
        formatted += "SCORING WEIGHTS\n";
        formatted += "-".repeat(60) + "\n";
        Object.entries(mergedData.scoring_weights).forEach(([key, value]) => {
          const formattedKey = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l: string) => l.toUpperCase());
          formatted += `  ${formattedKey}: ${value}\n`;
        });
        formatted += "\n";
      }

      // What We Look For
      if (mergedData?.what_we_look_for) {
        formatted += "What We Look For:\n";
        formatted += cleanTextForPDF(mergedData.what_we_look_for) + "\n\n";
      }

      // What working at G42 offers
      if (mergedData?.why_g42) {
        // formatted += "What working at G42 offers:\n";
        formatted += "What working at Accellor offers:\n";
        if (Array.isArray(mergedData.why_g42)) {
          mergedData.why_g42.forEach((item: any) => {
            if (item.type && item.value) {
              formatted += `${cleanTextForPDF(item.type)}: ${cleanTextForPDF(item.value)}\n`;
            } else {
              formatted += `${cleanTextForPDF(item)}\n`;
            }
          });
        } else {
          formatted += cleanTextForPDF(mergedData.why_g42) + "\n";
        }
        formatted += "\n";
      }

      // Application
      if (mergedData?.application) {
        formatted += "Application:\n";
        formatted += cleanTextForPDF(mergedData.application) + "\n";
      }

      return formatted;
    }

    if (agentId === "scoring_agent") {
      const answer = safeJSONParse(parsedContent?.answer);
      let formatted = "";

      if (
        Array.isArray(answer?.top_candidates) &&
        answer.top_candidates.length > 0
      ) {
        // Evaluation Criteria
        formatted += "Selection Criteria:\n";
        formatted += "- Alignment with functional and technical requirements\n";
        formatted += "- Strength of preferred qualification scores\n";
        formatted += "- Positive historical interview feedback\n";
        formatted += "- Current role, company, and location\n";
        formatted += "- Evidence from first-round interview responses\n\n";
        formatted += "=".repeat(70) + "\n\n";

        answer.top_candidates.forEach((candidate: any, idx: number) => {
          formatted += `CANDIDATE ${idx + 1}: ${cleanTextForPDF(candidate.name)}\n`;
          formatted += "=".repeat(70) + "\n\n";

          // Employment & Location
          formatted += "CURRENT EMPLOYMENT & LOCATION\n";
          formatted += "-".repeat(70) + "\n";
          formatted += `Title: ${cleanTextForPDF(candidate.current_title || "N/A")}\n`;
          formatted += `Company: ${cleanTextForPDF(candidate.current_company || "N/A")}\n`;
          formatted += `Location: ${cleanTextForPDF(candidate.current_location || "N/A")}\n`;
          if (candidate.linkedin_url) {
            formatted += `LinkedIn: ${cleanTextForPDF(candidate.linkedin_url)}\n`;
          }
          formatted += "\n";

          // Required Qualifications
          const reqQual = candidate.skills_match?.required_qualification || {};
          const firstCandidate = answer.top_candidates[0];
          const baseReqQual =
            firstCandidate?.skills_match?.required_qualification || {};

          if (
            baseReqQual.education ||
            baseReqQual.location ||
            baseReqQual.experience !== undefined
          ) {
            formatted += "REQUIRED QUALIFICATIONS\n";
            formatted += "-".repeat(70) + "\n";

            if (baseReqQual.education) {
              const status =
                reqQual.is_education_qualified?.toLowerCase() === "yes"
                  ? "YES"
                  : "NO";
              formatted += `Education: ${cleanTextForPDF(baseReqQual.education)}\n`;
              formatted += `  Status: ${status}\n`;
            }

            if (baseReqQual.location) {
              const status =
                reqQual.is_location_qualified?.toLowerCase() === "yes"
                  ? "YES"
                  : "NO";
              formatted += `Location: ${cleanTextForPDF(baseReqQual.location)}\n`;
              formatted += `  Status: ${status}\n`;
            }

            if (baseReqQual.experience !== undefined) {
              const status =
                reqQual.is_experience_qualified?.toLowerCase() === "yes"
                  ? "YES"
                  : "NO";
              formatted += `Experience: ${cleanTextForPDF(baseReqQual.experience)}\n`;
              formatted += `  Status: ${status}\n`;
            }

            const reqMet = [
              reqQual.is_education_qualified?.toLowerCase() === "yes",
              reqQual.is_location_qualified?.toLowerCase() === "yes",
              reqQual.is_experience_qualified?.toLowerCase() === "yes",
            ].filter(Boolean).length;
            const reqTotal = 3;
            if (reqMet === reqTotal) {
              formatted += `Overall: EXCELLENT\n`;
            }
            formatted += "\n";
          }

          // Skills & Qualifications Fit
          if (
            candidate.search_score !== undefined ||
            (candidate.skills_match?.functional_skills &&
              candidate.skills_match.functional_skills.length > 0) ||
            (candidate.skills_match?.preferred_qualification &&
              candidate.skills_match.preferred_qualification.length > 0)
          ) {
            formatted += "SKILLS AND PREFERRED QUALIFICATIONS FIT\n";
            formatted += "-".repeat(70) + "\n";

            if (candidate.search_score !== undefined) {
              const percentage = Number(candidate.search_score).toFixed(2);
              formatted += `Overall Match Score: (${percentage}%)\n\n`;
            }

            // Functional/Technical Skills
            if (
              candidate.skills_match?.functional_skills &&
              candidate.skills_match.functional_skills.length > 0
            ) {
              formatted += "Functional/Technical Skills:\n";
              candidate.skills_match.functional_skills.forEach((skill: any) => {
                const status =
                  skill.result?.toLowerCase() === "yes" ? "YES" : "NO";
                formatted += `  [${status}] ${cleanTextForPDF(skill.skill)}\n`;
              });
              formatted += "\n";
            }

            // Preferred Qualifications
            if (
              candidate.skills_match?.preferred_qualification &&
              candidate.skills_match.preferred_qualification.length > 0
            ) {
              formatted += "Preferred Qualifications:\n";
              candidate.skills_match.preferred_qualification.forEach(
                (pref: any) => {
                  const status =
                    pref.result?.toLowerCase() === "yes" ? "YES" : "NO";
                  formatted += `  [${status}] ${cleanTextForPDF(pref.entity_type)}: ${cleanTextForPDF(pref.entity_value || "N/A")}\n`;
                  if (pref.resoning) {
                    formatted += `        ${cleanTextForPDF(pref.resoning)}\n`;
                  }
                }
              );
              formatted += "\n";
            }
          }

          // Past Interview Feedback
          if (candidate.hiring_recommendation || candidate.interview_feedback) {
            formatted += "PAST INTERVIEW FEEDBACK\n";
            formatted += "-".repeat(70) + "\n";

            if (candidate.hiring_recommendation) {
              let recommendation = cleanTextForPDF(
                candidate.hiring_recommendation
              );
              formatted += `Recommendation: ${recommendation.toUpperCase()}\n`;
            }

            if (
              candidate.interview_feedback &&
              candidate.interview_feedback.trim()
            ) {
              formatted += `Feedback: ${cleanTextForPDF(candidate.interview_feedback)}\n`;
            }
            formatted += "\n";
          }

          // Screening Questions
          if (
            candidate.resume_mining_answers &&
            candidate.resume_mining_answers.length > 0
          ) {
            formatted += "FIRST-INTERVIEW SCREENING QUESTIONS\n";
            formatted += "-".repeat(70) + "\n";

            const answeredYes = candidate.resume_mining_answers.filter(
              (qa: any) => qa.answer?.toLowerCase() === "yes"
            ).length;
            const total = candidate.resume_mining_answers.length;
            const percentage =
              total > 0 ? Math.round((answeredYes / total) * 100) : 0;

            if (percentage >= 60) {
              const level = percentage >= 80 ? "EXCELLENT" : "MODERATE";
              formatted += `Overall: ${level} (${answeredYes} of ${total} positive)\n\n`;
            }

            candidate.resume_mining_answers.forEach((qa: any, qIdx: number) => {
              const answerValue =
                qa.answer?.toLowerCase() === "yes" ? "YES" : "NO";
              formatted += `Q${qIdx + 1}. ${cleanTextForPDF(qa.question)}\n`;
              formatted += `    Answer: ${answerValue}\n`;
              if (qa.reasoning && qa.reasoning.trim()) {
                formatted += `    Reasoning: ${cleanTextForPDF(qa.reasoning)}\n`;
              }
              formatted += "\n";
            });
          }

          formatted += "\n";
        });
      } else {
        formatted = "No candidates found";
      }

      return formatted;
    }

    if (agentId === "translation_agent") {
      const markdownText = finalAnswer?.text || "";
      // Remove <mark> tags and their attributes
      let cleanedText = markdownText.replace(/<mark[^>]*>(.*?)<\/mark>/g, "$1");

      // Convert markdown formatting to plain text with proper formatting
      // Headers
      cleanedText = cleanedText.replace(/^### (.*?)$/gm, "\n$1\n");
      cleanedText = cleanedText.replace(/^## (.*?)$/gm, "\n$1\n");
      cleanedText = cleanedText.replace(/^# (.*?)$/gm, "\n$1\n");

      // Bold text
      cleanedText = cleanedText.replace(/\*\*(.*?)\*\*/g, "$1");

      // Remove extra newlines but preserve paragraph structure
      cleanedText = cleanedText.replace(/\n\n\n+/g, "\n\n");

      return cleanedText.trim();
    }

    if (
      agentId === "template_reviewer_agent" ||
      agentId === "TemplateReviewerAgent" ||
      agentId === "template_guided_intake_agent" ||
      agentId === "TemplateGuidedIntakeAgent"
    ) {
      const questionText = question || "";

      if (!questionText) return "";

      // Convert markdown to formatted text
      let formatted = questionText;

      // Convert headers (### -> section titles, ## -> main titles)
      formatted = formatted.replace(/^### (.+)$/gm, "\n$1\n" + "-".repeat(60));
      formatted = formatted.replace(/^## (.+)$/gm, "\n$1\n" + "=".repeat(60));

      // Convert markdown tables to plain text BEFORE removing bold text
      // This ensures we can properly identify header rows
      const tableRegex = /(?:^\|.+\|\s*\n)+/gm;
      formatted = formatted.replace(tableRegex, (table) => {
        const rows = table.trim().split('\n').filter(row => row.trim());

        if (rows.length === 0) return '\n';

        let plainTable = '\n';
        let isFirstDataRow = true;
        let maxLabelWidth = 0;

        // First pass: calculate max label width and identify rows
        const parsedRows = rows.map((row) => {
          // Skip separator rows (lines with just |---|---|)
          if (row.match(/^\|[\s\-:|]+\|$/)) return null;

          // Extract cell contents
          const cells = row.split('|')
            .map(cell => cell.trim())
            .filter(cell => cell);

          if (cells.length === 0) return null;

          return cells;
        }).filter(row => row !== null);

        // Calculate max width for first column (for alignment)
        parsedRows.forEach(cells => {
          if (cells && cells[0]) {
            // Remove bold markers for width calculation
            const cleanLabel = cells[0].replace(/\*\*/g, '');
            maxLabelWidth = Math.max(maxLabelWidth, cleanLabel.length);
          }
        });

        // Second pass: format rows
        parsedRows.forEach((cells, idx) => {
          if (!cells) return;

          // Remove bold formatting from cells
          const cleanCells = cells.map(cell => cell.replace(/\*\*/g, ''));

          if (cleanCells.length === 2) {
            // Two-column table: format as aligned "Label: Value"
            const label = cleanCells[0].padEnd(maxLabelWidth);
            plainTable += `  ${label} : ${cleanCells[1]}\n`;
          } else if (cleanCells.length === 1) {
            // Single column
            plainTable += `  ${cleanCells[0]}\n`;
          } else {
            // Multi-column: use column separators
            plainTable += `  ${cleanCells.join(' | ')}\n`;
          }
        });

        return plainTable + '\n';
      });

      // Convert bold text (after table processing)
      formatted = formatted.replace(/\*\*(.+?)\*\*/g, "$1");

      // Clean up horizontal rules
      formatted = formatted.replace(/^[\-=]{3,}$/gm, "");

      // Remove extra blank lines but preserve structure
      formatted = formatted.replace(/\n\n\n+/g, "\n\n");

      // Clean text for PDF rendering
      formatted = cleanTextForPDF(formatted);

      return formatted.trim();
    }

    if (
      agentId === "redrafter_orchestrator" ||
      agentId === "redrafting_agent" ||
      agentId === "contract_reviewer_orchestrator" ||
      agentId === "consolidator_agent" ||
      agentId === "ConsolidatorAgent"
    ) {
      // Check if this is supplier communication (email format)
      const supplierComm = parsedContent?.supplier_communication_redraft || parsedContent?.supplier_communication;

      if (supplierComm) {
        const { email_subject, email_body, email_footer } = supplierComm;
        let formatted = "";

        // Add subject
        if (email_subject) {
          formatted += `${email_subject}\n\n`;
          formatted += "=".repeat(60) + "\n\n";
        }

        // Add body
        if (email_body) {
          let cleanBody = email_body
            .replace(/\*\*(.+?)\*\*/g, "$1") // Remove bold
            .replace(/\*(.+?)\*/g, "$1") // Remove italic
            .replace(/^- (.+)$/gm, "• $1") // Bullet points
            .replace(/^#{1,6}\s(.+)$/gm, "$1") // Remove headers
            .trim();

          formatted += cleanBody + "\n\n";
        }

        // Add footer
        if (email_footer) {
          formatted += "-".repeat(60) + "\n\n";
          let cleanFooter = email_footer
            .replace(/\*\*(.+?)\*\*/g, "$1")
            .replace(/\*(.+?)\*/g, "$1")
            .trim();
          formatted += cleanFooter;
        }

        return cleanTextForPDF(formatted);
      }

      // Check if this is a redrafting response by looking for redrafted_contract
      const markdown = finalAnswer?.result?.redrafted_contract || finalAnswer?.redrafted_contract || "";

      if (!markdown) return "";

      // First, convert escaped newlines and quotes to actual characters
      let cleanedText = String(markdown)
        .replace(/\\n/g, "\n")      // Convert escaped newlines
        .replace(/\\"/g, '"')       // Convert escaped quotes
        .replace(/\\t/g, " ")       // Convert tabs to spaces
        .replace(/\\/g, "")         // Remove remaining backslashes
        .trim();

      // Remove markdown formatting while preserving text content

      // Remove headers but preserve the text exactly
      cleanedText = cleanedText.replace(/^#{1,6}\s+/gm, "");

      // Remove bold - use non-greedy matching to avoid issues
      cleanedText = cleanedText.replace(/\*\*([^\*]+)\*\*/g, "$1");
      cleanedText = cleanedText.replace(/__([^_]+)__/g, "$1");

      // Remove italic - use non-greedy matching
      cleanedText = cleanedText.replace(/\*([^\*\n]+)\*/g, "$1");
      cleanedText = cleanedText.replace(/_([^_\n]+)_/g, "$1");

      // Remove links but keep the text exactly
      cleanedText = cleanedText.replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1");

      // Remove inline code
      cleanedText = cleanedText.replace(/`([^`]+)`/g, "$1");

      // Remove code blocks
      cleanedText = cleanedText.replace(/```[\s\S]*?```/g, "");

      // Remove horizontal rules
      cleanedText = cleanedText.replace(/^[\-\*_]{3,}$/gm, "");

      // Remove blockquotes
      cleanedText = cleanedText.replace(/^>\s*/gm, "");

      // Remove list markers
      cleanedText = cleanedText.replace(/^[\*\-\+]\s+/gm, "• ");
      cleanedText = cleanedText.replace(/^\d+\.\s+/gm, "");

      // Clean up excessive newlines but preserve structure
      cleanedText = cleanedText.replace(/\n{3,}/g, "\n\n");

      // Normalize text for PDF rendering (handle special characters)
      cleanedText = normalizeTextForPDF(cleanedText);

      // Trim and return
      return cleanedText.trim();
    }

    // Fallback: if no agent-specific formatting matched, try to extract clean content
    if (finalAnswer) {
      // Try to extract text content from various common structures
      if (typeof finalAnswer === 'string') {
        return cleanTextForPDF(finalAnswer);
      }
      if (finalAnswer.text) {
        return cleanTextForPDF(finalAnswer.text);
      }
      if (finalAnswer.content) {
        return cleanTextForPDF(finalAnswer.content);
      }
      if (finalAnswer.message) {
        return cleanTextForPDF(finalAnswer.message);
      }
    }

    // Last resort: clean the raw content
    const cleanedContent = cleanTextForPDF(content);

    // If the cleaned content still looks like JSON, try to pretty-format it
    if (cleanedContent.includes('{') || cleanedContent.includes('[')) {
      try {
        const parsed = JSON.parse(content);
        return JSON.stringify(parsed, null, 2);
      } catch {
        // Return the cleaned version even if it contains JSON-like characters
        return cleanedContent;
      }
    }

    return cleanedContent;
  } catch (error) {
    console.error("Error formatting content for PDF:", error);
    // Return cleaned version of original content as ultimate fallback
    return cleanTextForPDF(String(content));
  }
};

// Helper function to extract custom PDF header for specific agents
export const getCustomPDFHeader = (
  content: string,
  agentId?: string
): string | null => {
  try {
    if (agentId === "synthetic_resume_agent" || agentId === "skill_inference_agent" || agentId === "jd_composer_agent" || agentId === "modify_requirements" || agentId === "jd_details_agent") {
      const parsedContent = safeJSONParse(content);

      // Parse the answer field first
      const answer = safeJSONParse(parsedContent?.answer);

      // Check if data is inside an 'output' field
      let outputData = answer;
      if (typeof parsedContent?.output === "string") {
        outputData = safeJSONParse(parsedContent.output);
      } else if (parsedContent?.output && typeof parsedContent.output === "object") {
        outputData = parsedContent.output;
      }

      // Parse skill_inference_agent or jd_composer_agent data from answer or output
      let skillInferenceData = {};
      if (typeof answer?.jd_composer_agent === "string") {
        skillInferenceData = safeJSONParse(answer.jd_composer_agent);
      } else if (typeof outputData?.jd_composer_agent === "string") {
        skillInferenceData = safeJSONParse(outputData.jd_composer_agent);
      } else if (typeof answer?.skill_inference_agent === "string") {
        skillInferenceData = safeJSONParse(answer.skill_inference_agent);
      } else if (typeof outputData?.skill_inference_agent === "string") {
        skillInferenceData = safeJSONParse(outputData.skill_inference_agent);
      } else if (answer?.jd_composer_agent) {
        skillInferenceData = answer.jd_composer_agent;
      } else if (outputData?.jd_composer_agent) {
        skillInferenceData = outputData.jd_composer_agent;
      } else if (answer?.skill_inference_agent) {
        skillInferenceData = answer.skill_inference_agent;
      } else if (outputData?.skill_inference_agent) {
        skillInferenceData = outputData.skill_inference_agent;
      }

      // Get job_type from skill_inference_agent or answer
      const jobType = skillInferenceData?.job_type || answer?.job_type;

      if (jobType) {
        return `${jobType.toUpperCase()} - SKILLS & QUALIFICATIONS`;
      }
    }

    if (agentId === "scoring_agent") {
      return "CANDIDATE EVALUATION";
    }

    if (agentId === "translation_agent") {
      return "CONTENT TRANSLATION";
    }

    if (agentId === "template_reviewer_agent" || agentId === "TemplateReviewerAgent") {
      return "TEMPLATE REVIEW";
    }

    if (agentId === "template_guided_intake_agent" || agentId === "TemplateGuidedIntakeAgent") {
      return "TEMPLATE GUIDED INTAKE";
    }

    if (
      agentId === "contract_reviewer_orchestrator" ||
      agentId === "consolidator_agent" ||
      agentId === "ConsolidatorAgent"
    ) {
      // Check if this is supplier communication
      const parsedContent = safeJSONParse(content);
      const supplierComm = parsedContent?.supplier_communication_redraft || parsedContent?.supplier_communication;
      if (supplierComm) {
        return "SUPPLIER COMMUNICATION";
      }
    }

    // No custom header for redrafter_orchestrator - content only
    if (agentId === "redrafter_orchestrator") {
      return null;
    }

    return null;
  } catch (error) {
    console.error("Error extracting custom PDF header:", error);
    return null;
  }
};

// Helper function to generate custom file names for PDF downloads
export const generatePDFFileName = (
  content: string,
  agentId?: string
): string => {
  try {
    // Handle specific agents with custom file names
    if (agentId === "synthetic_resume_agent" || agentId === "skill_inference_agent" || agentId === "jd_composer_agent" || agentId === "modify_requirements" || agentId === "jd_details_agent") {
      const parsedContent = safeJSONParse(content);

      // Parse the answer field first
      const answer = safeJSONParse(parsedContent?.answer);

      // Check if data is inside an 'output' field
      let outputData = answer;
      if (typeof parsedContent?.output === "string") {
        outputData = safeJSONParse(parsedContent.output);
      } else if (parsedContent?.output && typeof parsedContent.output === "object") {
        outputData = parsedContent.output;
      }

      // Parse skill_inference_agent or jd_composer_agent data from answer or output
      let skillInferenceData = {};
      if (typeof answer?.jd_composer_agent === "string") {
        skillInferenceData = safeJSONParse(answer.jd_composer_agent);
      } else if (typeof outputData?.jd_composer_agent === "string") {
        skillInferenceData = safeJSONParse(outputData.jd_composer_agent);
      } else if (typeof answer?.skill_inference_agent === "string") {
        skillInferenceData = safeJSONParse(answer.skill_inference_agent);
      } else if (typeof outputData?.skill_inference_agent === "string") {
        skillInferenceData = safeJSONParse(outputData.skill_inference_agent);
      } else if (answer?.jd_composer_agent) {
        skillInferenceData = answer.jd_composer_agent;
      } else if (outputData?.jd_composer_agent) {
        skillInferenceData = outputData.jd_composer_agent;
      } else if (answer?.skill_inference_agent) {
        skillInferenceData = answer.skill_inference_agent;
      } else if (outputData?.skill_inference_agent) {
        skillInferenceData = outputData.skill_inference_agent;
      }

      // Get job_type from skill_inference_agent or answer
      const jobType = skillInferenceData?.job_type || answer?.job_type || "Position";
      // Sanitize job type for file name (remove special characters)
      const sanitizedJobType = jobType.replace(/[^a-zA-Z0-9\s-]/g, "").trim();
      return `${sanitizedJobType} - Skills & qualifications.pdf`;
    }

    if (agentId === "scoring_agent") {
      return "Candidate Evaluation.pdf";
    }

    if (agentId === "translation_agent") {
      return `Content Translation.pdf`;
    }

    if (agentId === "template_reviewer_agent" || agentId === "TemplateReviewerAgent") {
      const parsedContent = safeJSONParse(content);
      const question = parsedContent?.question || "";

      // Try to extract template name from the question text
      const businessUnitMatch = question.match(/\*\*Business Unit:\*\*\s*([^\n]+)/);
      const templateMatch = question.match(/\*\*Template:\*\*\s*([^\n]+)/);

      if (businessUnitMatch && templateMatch) {
        const businessUnit = businessUnitMatch[1].trim();
        const templateName = templateMatch[1].trim();
        const sanitized = `${businessUnit} - ${templateName}`.replace(/[^a-zA-Z0-9\s\-]/g, "").trim();
        return `${sanitized} - Template Review.pdf`;
      } else if (templateMatch) {
        const templateName = templateMatch[1].trim().replace(/[^a-zA-Z0-9\s\-]/g, "").trim();
        return `${templateName} - Template Review.pdf`;
      }

      return "Template Review.pdf";
    }

    if (agentId === "template_guided_intake_agent" || agentId === "TemplateGuidedIntakeAgent") {
      const parsedContent = safeJSONParse(content);
      const question = parsedContent?.question || "";

      // Try to extract template name from the question text
      const businessUnitMatch = question.match(/\*\*Business Unit:\*\*\s*([^\n]+)/);
      const templateMatch = question.match(/\*\*Template:\*\*\s*([^\n]+)/);

      if (businessUnitMatch && templateMatch) {
        const businessUnit = businessUnitMatch[1].trim();
        const templateName = templateMatch[1].trim();
        const sanitized = `${businessUnit} - ${templateName}`.replace(/[^a-zA-Z0-9\s\-]/g, "").trim();
        return `${sanitized} - Template Intake.pdf`;
      } else if (templateMatch) {
        const templateName = templateMatch[1].trim().replace(/[^a-zA-Z0-9\s\-]/g, "").trim();
        return `${templateName} - Template Intake.pdf`;
      }

      return "Template Intake.pdf";
    }

    if (
      agentId === "contract_reviewer_orchestrator" ||
      agentId === "consolidator_agent" ||
      agentId === "ConsolidatorAgent"
    ) {
      const parsedContent = safeJSONParse(content);
      const supplierComm = parsedContent?.supplier_communication_redraft || parsedContent?.supplier_communication;
      if (supplierComm && supplierComm.email_subject) {
        // Use email subject as filename
        const sanitizedSubject = supplierComm.email_subject
          .replace(/[^a-zA-Z0-9\s\-]/g, "")
          .trim()
          .substring(0, 100); // Limit length
        return `${sanitizedSubject}.pdf`;
      }
      return "Supplier Communication.pdf";
    }

    if (agentId === "redrafter_orchestrator") {
      const parsedContent = safeJSONParse(content);
      const finalAnswer = safeJSONParse(parsedContent?.finalAnswer);
      const fileName = finalAnswer?.result?.file_name || finalAnswer?.file_name || "Redrafted Contract";
      // Sanitize file name and ensure it ends with .pdf
      const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9\s\-_\.]/g, "").trim();
      // Add .pdf extension if not present
      return sanitizedFileName.endsWith(".pdf") ? sanitizedFileName : `${sanitizedFileName}.pdf`;
    }

    // Default file name pattern for other agents
    const agentName = agentId
      ? agentId.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      : "Agent";
    return `${agentName}_response_${new Date().getTime()}.pdf`;
  } catch (error) {
    console.error("Error generating PDF file name:", error);
    return `download_${new Date().getTime()}.pdf`;
  }
};
